import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAdminUserHref } from '@/modules/admin/client/utils/member-url';
import { useTranslation } from 'react-i18next';

import AboutMe from './AboutMe.component';
import AvatarNameMobile from './AvatarNameMobile.component';
import BlockedMemberBanner from './BlockedMemberBanner.component';
import BlockMember from './BlockMember.component';
import BottomNavigationSmall from './BottomNavigationSmall.component';
import ConfirmEmailNotification from './ConfirmEmailNotification.component';
import MembershipsList from './MembershipsList.component';
import ProfileOverview from './ProfileOverview.component';
import ProfileTabs from './ProfileTabs.component';
import ProfileTribesTab from './ProfileTribesTab.component';
import TopNavigationSmall from './TopNavigationSmall.component';
import TribesInCommon from './TribesInCommon.component';
import UserDoesNotExist from './UserDoesNotExist.component';
import { fetch as fetchProfile } from '../api/users.api';
import { getErrorResponse } from '@/modules/core/client/api/http-client';
import {
  getMobileProfileRedirect,
  getProfileViewTab,
  getProfileViewTabStateName,
} from '../utils/profile-routes';
import { getCurrentRouteParams } from '@/modules/core/client/services/client-runtime';
import { getProfileExperiencesPath } from '@/modules/core/shared/navigation-links';
import { useAuth } from '@/modules/core/client/react-app/auth';
import { useSettings } from '@/modules/core/client/react-app/app-context';
import { useCurrentPath } from '@/modules/core/client/react-app/useCurrentPath';
import ContactList from '@/modules/contacts/client/components/ContactList.component';
import RemoveContactContainer from '@/modules/contacts/client/components/RemoveContactContainer';
import * as contactsApi from '@/modules/contacts/client/api/contacts.api';
import CreateExperience from '@/modules/experiences/client/components/CreateExperience.component';
import { readReplyRecipient } from '@/modules/experiences/client/api/experiences.api';
import ListExperiences from '@/modules/experiences/client/components/ListExperiences.component';
import Experience from '@/modules/experiences/client/components/read-experiences/Experience';
import type { ExperienceMine } from '@/modules/experiences/shared/experience';
import Offers from '@/modules/offers/client/components/Offers.component';
import ContactsCommon from '@/modules/contacts/client/components/ContactsCommon.component';
import ReportMember from '@/modules/support/client/components/ReportMember.component';
import Tooltip from '@/modules/core/client/components/Tooltip';
import type {
  ContactList as ContactListData,
  ContactRecord,
} from '@/modules/contacts/client/types';
import type { UserProfile } from '../types';

interface ResolvedUserProfile extends UserProfile {
  $resolved?: boolean;
}

type ContactState = ContactRecord & { $resolved: boolean };

const TooltipWithContent = Tooltip as unknown as React.ComponentType<{
  tooltip: React.ReactNode;
  placement: 'bottom';
  children: React.ReactNode;
}>;
const ReportMemberButton = ReportMember as unknown as React.ComponentType<{
  className: string;
  username: string;
}>;

function normalizeContact(contact: ContactRecord | null): ContactState {
  if (!contact) {
    return { $resolved: true };
  }

  const normalized: ContactState = { ...contact, $resolved: true };

  if (typeof normalized.userFrom === 'object') {
    normalized.userFrom = normalized.userFrom._id;
  }

  if (typeof normalized.userTo === 'object') {
    normalized.userTo = normalized.userTo._id;
  }

  return normalized;
}

function ProfileAboutTab({
  authUser,
  contacts,
  isSelf,
  profile,
  profileMinimumLength,
}: {
  authUser: UserProfile;
  contacts: ContactListData;
  isSelf: boolean;
  profile: ResolvedUserProfile;
  profileMinimumLength: number;
}) {
  const { t } = useTranslation('users') as {
    t: (key: string, options?: Record<string, unknown>) => string;
  };
  const showContactsCommon =
    profile._id !== authUser._id && (contacts?.length || 0) > 0;
  const showCircles = isSelf || (profile.member && profile.member.length > 0);

  return (
    <div className="row">
      <div className="col-md-6">
        <AboutMe
          isSelf={isSelf}
          profile={profile}
          profileMinimumLength={profileMinimumLength}
        />
      </div>
      <div className="col-md-6">
        <div className="hidden-xs">
          <Offers authUser={authUser} profile={profile} />
        </div>
        {showContactsCommon && <ContactsCommon profileId={profile._id} />}
        {showCircles && (
          <section className="panel panel-default">
            <header className="panel-heading">{t('Circles') as string}</header>
            <div className="panel-body">
              {!isSelf && (
                <TribesInCommon
                  memberIds={authUser.memberIds || []}
                  memberships={profile.member}
                />
              )}
              <MembershipsList
                isOwnProfile={isSelf}
                memberships={profile.member}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function ProfileDesktopActions({
  isAdmin,
  isSelf,
  contact,
  contactResolved,
  onRemoveClick,
  profile,
  referencesEnabled,
}: {
  isAdmin: boolean;
  isSelf: boolean;
  contact: ContactState;
  contactResolved: boolean;
  onRemoveClick: () => void;
  profile: ResolvedUserProfile;
  referencesEnabled: boolean;
}) {
  const { t } = useTranslation('users') as {
    t: (key: string, options?: Record<string, unknown>) => string;
  };

  return (
    <ul className="nav nav-pills nav-narrow profile-actions" role="navigation">
      {!isSelf && (
        <>
          <li>
            <a href={`/messages/${profile.username}`} className="btn btn-link">
              <i className="fa icon-message-alt" />
              {t('Send a message') as string}
            </a>
          </li>
          {referencesEnabled && (
            <li>
              <a
                className="btn btn-link"
                href={getProfileExperiencesPath(profile.username)}
              >
                <i className="icon-plus-squared-alt" />
                {t('Share your experience') as string}
              </a>
            </li>
          )}
          <li>
            {contactResolved && !contact._id && (
              <a className="btn btn-link" href={`/contact-add/${profile._id}`}>
                <i className="icon-plus-squared-alt" />
                {t('Add contact') as string}
              </a>
            )}
            {contactResolved && contact._id && (
              <TooltipWithContent
                tooltip={
                  contact.confirmed
                    ? (t('Contacts since {{date, LL}}', {
                        date: new Date(contact.created || ''),
                      }) as string)
                    : (t('Request sent {{date, LL}}', {
                        date: new Date(contact.created || ''),
                      }) as string)
                }
                placement="bottom"
              >
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={onRemoveClick}
                >
                  <i className="icon-minus-squared-alt" />
                  {contact.confirmed
                    ? (t('Remove contact') as string)
                    : (t('Delete contact request') as string)}
                </button>
              </TooltipWithContent>
            )}
          </li>
        </>
      )}
      {isAdmin && (
        <li>
          <a className="btn btn-link" href={getAdminUserHref(profile)}>
            <i className="icon-cog" aria-hidden="true" />
            Admin
          </a>
        </li>
      )}
    </ul>
  );
}

export default function ProfilePage({
  currentPath: routedPath,
  embedded = false,
  profileUsername,
  user: authUser,
}: {
  currentPath?: string;
  embedded?: boolean;
  profileUsername?: string;
  user: UserProfile;
}) {
  const { t } = useTranslation('users') as {
    t: (key: string, options?: Record<string, unknown>) => string;
  };
  const { setUser } = useAuth() as { setUser: (user: UserProfile) => void };
  const browserPath = useCurrentPath();
  const currentPath = routedPath || browserPath;
  const username = profileUsername || getCurrentRouteParams().username;
  const { profileMinimumLength = 140, referencesEnabled = false } =
    useSettings();

  const [profile, setProfile] = useState<ResolvedUserProfile | null>(null);
  const [contact, setContact] = useState<ContactState>({ $resolved: false });
  const [contacts, setContacts] = useState<ContactListData>(
    Object.assign([], { $resolved: false }),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [experienceOnly, setExperienceOnly] = useState(false);
  const [ownExperience, setOwnExperience] = useState<
    ExperienceMine | undefined
  >();
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const tab = getProfileViewTab(currentPath, username);
  const activePathName = getProfileViewTabStateName(currentPath, username);
  const canLoadReply = referencesEnabled && !embedded;
  const isCreatingExperience = tab === 'experiences-new';
  const isSelf = profile?._id === authUser?._id;

  const removeContact = useCallback((removedContact: ContactRecord) => {
    setContacts(previousContacts => {
      const nextContacts = [...previousContacts];
      const index = nextContacts.findIndex(
        item => item._id === removedContact._id,
      );

      if (index !== -1) {
        nextContacts.splice(index, 1);
      }

      return Object.assign(nextContacts, {
        $resolved: true,
        length: nextContacts.length,
      });
    });

    setContact(previousContact => {
      if (previousContact?._id !== removedContact._id) {
        return previousContact;
      }

      return { $resolved: true };
    });
  }, []);

  useEffect(() => {
    if (embedded) {
      return;
    }

    const redirectPath = getMobileProfileRedirect(currentPath, username);

    if (redirectPath && redirectPath !== currentPath) {
      window.location.assign(redirectPath);
    }
  }, [currentPath, embedded, username]);

  useEffect(() => {
    let isMounted = true;

    async function loadProfile() {
      setIsLoading(true);
      setExperienceOnly(false);
      setOwnExperience(undefined);
      setContact({ $resolved: false });
      setContacts(Object.assign([], { $resolved: false }));

      try {
        let loadedProfile;
        let replyOnly = false;
        try {
          loadedProfile = await fetchProfile(username);
        } catch (error) {
          if (!canLoadReply || getErrorResponse(error)?.status !== 404)
            throw error;
          loadedProfile = await readReplyRecipient(username);
          setOwnExperience(loadedProfile.ownExperience);
          replyOnly = true;
        }

        if (!isMounted) {
          return;
        }

        if (!loadedProfile?._id) {
          setProfile({
            _id: '',
            username: '',
            displayName: '',
            $resolved: true,
          });
          setIsLoading(false);
          return;
        }

        setProfile({ ...loadedProfile, $resolved: true });
        setExperienceOnly(replyOnly);
        if (replyOnly) return;

        const viewingOwnProfile = loadedProfile._id === authUser?._id;
        const [loadedContact, loadedContacts] = await Promise.all([
          viewingOwnProfile || !authUser?._id
            ? Promise.resolve(null)
            : contactsApi.getByUserId(loadedProfile._id).catch(() => null),
          contactsApi.list(loadedProfile._id).catch(() => []),
        ]);

        if (!isMounted) {
          return;
        }

        setContact(normalizeContact(loadedContact));
        setContacts(
          Object.assign([...(loadedContacts || [])], {
            $resolved: true,
            length: loadedContacts?.length || 0,
          }),
        );
      } catch {
        if (isMounted) {
          setProfile({
            _id: '',
            username: '',
            displayName: '',
            $resolved: true,
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [authUser?._id, username, canLoadReply, isCreatingExperience]);

  const handleMembershipUpdated = (data: { user?: UserProfile } | null) => {
    if (data?.user) {
      setUser(data.user);
    }
  };

  const tabContent = useMemo(() => {
    if (!profile?._id) {
      return null;
    }

    switch (tab) {
      case 'overview':
        return (
          <>
            <AvatarNameMobile profile={profile} isSelf={isSelf} />
            <ProfileOverview profile={profile} isSelf={isSelf} />
            {!isSelf && (
              <div className="profile-flags">
                <ReportMemberButton
                  className="btn btn-sm btn-default"
                  username={profile.username}
                />
                <br />
                <br />
                <BlockMember
                  className="btn btn-sm btn-default"
                  isBlocked={authUser.blocked?.includes(profile._id) === true}
                  username={profile.username}
                />
              </div>
            )}
          </>
        );
      case 'accommodation':
        return <Offers authUser={authUser} profile={profile} />;
      case 'contacts':
        return (
          <ContactList
            appUser={authUser}
            contacts={contacts}
            onContactRemoved={removeContact}
          />
        );
      case 'tribes':
        return (
          <ProfileTribesTab
            memberships={
              /* istanbul ignore next -- public profile records always provide membership arrays. */
              profile.member || []
            }
            onMembershipUpdated={handleMembershipUpdated}
            user={authUser}
          />
        );
      case 'experiences':
        return referencesEnabled ? (
          <ListExperiences authenticatedUser={authUser} profile={profile} />
        ) : null;
      case 'experiences-new':
        return referencesEnabled ? (
          <CreateExperience userFrom={authUser} userTo={profile} />
        ) : null;
      case 'about':
      default:
        return (
          <ProfileAboutTab
            authUser={authUser}
            contacts={contacts}
            isSelf={isSelf}
            profile={profile}
            profileMinimumLength={profileMinimumLength}
          />
        );
    }
  }, [
    authUser,
    contacts,
    isSelf,
    profile,
    profileMinimumLength,
    referencesEnabled,
    removeContact,
    tab,
  ]);

  const pendingIncomingContact =
    contact.$resolved &&
    contact._id &&
    !contact.confirmed &&
    contact.userTo === authUser._id;

  if (experienceOnly && !isLoading && profile && canLoadReply) {
    return (
      <section className="container container-spacer">
        <h2>{profile.displayName}</h2>
        <ReportMemberButton
          className="btn btn-sm btn-default"
          username={profile.username}
        />
        {isCreatingExperience ? (
          <CreateExperience userFrom={authUser} userTo={profile} />
        ) : ownExperience ? (
          <Experience
            experience={{
              ...ownExperience,
              userFrom: { _id: authUser._id, username: authUser.username },
              userTo: profile,
              response: null,
            }}
            onReceiverProfile={false}
          />
        ) : (
          <p>
            <a href={getProfileExperiencesPath(profile.username)}>
              {t('Share your experience') as string}
            </a>
          </p>
        )}
      </section>
    );
  }

  return (
    <>
      {!embedded && (
        <TopNavigationSmall
          isAdmin={authUser.roles?.includes('admin') || false}
          contact={contact}
          isResolved={contact.$resolved}
          onContactRemoved={removeContact}
          referencesEnabled={referencesEnabled}
          selfId={authUser._id}
          userId={profile?._id || ''}
          username={profile?.username || username}
        />
      )}
      {!embedded && (
        <BottomNavigationSmall
          contactCount={contacts.length || 0}
          isSelf={isSelf}
          username={profile?.username || username}
        />
      )}

      <section
        className={
          embedded
            ? 'container-spacer profile-view profile-view-embedded'
            : 'container container-spacer profile-view'
        }
      >
        {profile && (authUser.blocked || []).includes(profile._id) && (
          <BlockedMemberBanner username={profile.username} />
        )}

        {isLoading && (
          <div
            className="row content-wait"
            role="alertdialog"
            aria-busy="true"
            aria-live="assertive"
          >
            <small>{t('Wait a moment…') as string}</small>
          </div>
        )}

        {!isLoading &&
          profile?.$resolved &&
          !profile.username &&
          authUser.public !== false && <UserDoesNotExist />}

        {!isLoading &&
          profile?.$resolved &&
          isSelf &&
          authUser.public === false && <ConfirmEmailNotification />}

        {!isLoading && profile?.username && profile.$resolved && (
          <>
            {pendingIncomingContact && (
              <div
                className="row"
                role="alertdialog"
                aria-labelledby="confirmContactDialogLabel"
              >
                <div className="col-xs-12 text-center">
                  <ul className="list-inline">
                    <li id="confirmContactDialogLabel">
                      <strong>{profile.displayName}</strong>{' '}
                      {t('sent you a contact request') as string}
                    </li>
                    <li>
                      <a
                        className="btn btn-sm btn-primary"
                        href={`/contact-confirm/${contact._id}`}
                      >
                        {t('Confirm Request') as string}
                      </a>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => setShowRemoveModal(true)}
                      >
                        {t('Decline Request') as string}
                      </button>
                    </li>
                  </ul>
                  <hr />
                </div>
              </div>
            )}

            <div className="row">
              {tab === 'overview' ? (
                <div className="col-sm-12">{tabContent}</div>
              ) : (
                <>
                  <div className="col-sm-3 hidden-xs">
                    <ProfileOverview profile={profile} isSelf={isSelf} />
                    {!isSelf && (
                      <div className="profile-flags">
                        <ReportMemberButton
                          className="btn btn-sm btn-default"
                          username={profile.username}
                        />
                        <br />
                        <br />
                        <BlockMember
                          className="btn btn-sm btn-default"
                          isBlocked={(authUser.blocked || []).includes(
                            profile._id,
                          )}
                          username={profile.username}
                        />
                      </div>
                    )}
                  </div>
                  <div className="col-sm-9">
                    <div className="row hidden-xs">
                      <div className="col-xs-12">
                        <h2 className="profile-name">{profile.displayName}</h2>
                        <h4 className="text-muted profile-username">
                          @{profile.username}
                        </h4>
                        {isSelf && (
                          <a
                            href="/profile/edit"
                            className="btn btn-primary pull-right"
                          >
                            {t('Edit your profile') as string}
                          </a>
                        )}
                        {profile.tagline && (
                          <div className="profile-tagline">
                            {profile.tagline}
                          </div>
                        )}
                      </div>
                    </div>

                    {(!isSelf || authUser.roles?.includes('admin')) && (
                      <div className="row hidden-xs">
                        <div className="col-xs-12">
                          <ProfileDesktopActions
                            isAdmin={authUser.roles?.includes('admin') || false}
                            isSelf={isSelf}
                            contact={contact}
                            contactResolved={contact.$resolved}
                            onRemoveClick={() => setShowRemoveModal(true)}
                            profile={profile}
                            referencesEnabled={referencesEnabled}
                          />
                        </div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-xs-12">
                        <ProfileTabs
                          activePathName={activePathName}
                          contactsCount={contacts.length || 0}
                          initialPathName={activePathName}
                          isExperiencesEnabled={referencesEnabled}
                          isOWnProfile={isSelf}
                          userId={profile._id}
                          username={profile.username}
                        />
                        {tabContent}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </section>

      {contact._id && (
        <RemoveContactContainer
          contact={contact as ContactRecord & { _id: string }}
          onCancel={() => setShowRemoveModal(false)}
          onSuccess={() => {
            setShowRemoveModal(false);
            removeContact(contact);
          }}
          selfId={authUser._id}
          show={showRemoveModal}
        />
      )}
    </>
  );
}
